import { type ReactNode, useCallback, useEffect, useRef, useState } from "react";
import { ChevronDown } from "lucide-react";

/* ------------------------------------------------------------------ */
/*  SplitButton                                                        */
/* ------------------------------------------------------------------ */

interface SplitButtonProps {
  /** Text for the main button */
  label: string;
  /** Click handler for the main button */
  onClick: () => void;
  /** Whether the main button is disabled */
  disabled?: boolean;
  /** Menu items rendered in the dropdown */
  children: ReactNode;
  /** Additional className on the outer wrapper */
  className?: string;
}

export function SplitButton({
  label,
  onClick,
  disabled,
  children,
  className = "",
}: SplitButtonProps) {
  const [open, setOpen] = useState(false);
  const caretRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    function handleClick(e: MouseEvent) {
      if (
        menuRef.current &&
        !menuRef.current.contains(e.target as Node) &&
        caretRef.current &&
        !caretRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [open]);

  // Close on Escape and restore focus
  useEffect(() => {
    if (!open) return;
    function handleKey(e: KeyboardEvent) {
      if (e.key === "Escape") {
        setOpen(false);
        caretRef.current?.focus();
      }
    }
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [open]);

  return (
    <div className={`relative inline-flex ${className}`}>
      {/* Main action */}
      <button
        type="button"
        onClick={onClick}
        disabled={disabled}
        className="bg-tide text-white hover:bg-tide-d border border-tide font-semibold text-[13px] rounded-l-[10px] px-3.5 py-2 disabled:opacity-45 disabled:cursor-default transition-colors"
      >
        {label}
      </button>

      {/* Caret toggle */}
      <button
        ref={caretRef}
        type="button"
        aria-haspopup="true"
        aria-expanded={open}
        aria-label="More actions"
        onClick={() => setOpen((prev) => !prev)}
        className="bg-tide text-white hover:bg-tide-d border border-tide border-l border-l-white/25 font-semibold rounded-r-[10px] px-2 py-2 transition-colors"
      >
        <ChevronDown size={14} strokeWidth={2} />
      </button>

      {/* Dropdown menu */}
      {open && (
        <div
          ref={menuRef}
          role="menu"
          onClick={() => setOpen(false)}
          className="absolute top-full mt-1.5 right-0 w-[220px] bg-paper-hi border border-ink-6 rounded-[12px] py-1.5 shadow-lift z-[70]"
        >
          {children}
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  SplitMenuItem                                                      */
/* ------------------------------------------------------------------ */

interface SplitMenuItemProps {
  icon?: ReactNode;
  children: ReactNode;
  onClick: () => void;
  disabled?: boolean;
  /** "danger" renders in rust color */
  variant?: "default" | "danger";
  /** e.g. "Cmd+S" — right-aligned hint */
  kbd?: string;
}

export function SplitMenuItem({
  icon,
  children,
  onClick,
  disabled,
  variant = "default",
  kbd,
}: SplitMenuItemProps) {
  const handleClick = useCallback(() => {
    if (!disabled) onClick();
  }, [disabled, onClick]);

  const base =
    "flex items-center gap-2.5 w-full px-3 py-2 rounded-lg text-[13px] font-semibold transition-colors";
  const variants = {
    default:
      "text-ink-2 hover:bg-paper-2 disabled:opacity-45 disabled:cursor-default",
    danger:
      "text-rust hover:bg-rust/5 disabled:opacity-45 disabled:cursor-default",
  };

  return (
    <button
      type="button"
      role="menuitem"
      disabled={disabled}
      onClick={handleClick}
      className={`${base} ${variants[variant]}`}
    >
      {icon}
      <span className="flex-1 text-left">{children}</span>
      {kbd && (
        <span className="ml-auto font-mono text-[11px] text-ink-5 font-medium">
          {kbd}
        </span>
      )}
    </button>
  );
}

/* ------------------------------------------------------------------ */
/*  SplitMenuDivider                                                   */
/* ------------------------------------------------------------------ */

export function SplitMenuDivider() {
  return <div role="separator" className="h-px bg-ink-6 my-1 mx-2" />;
}
